import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Api from "../utils/Api";
import closeIcon from "../../images/IconAnnouncementExit.svg";
import dayjs from "dayjs";
import { useSessionStore } from "../storez/SessionStore";
import { useFeatureFlagStore } from "../storez/FeatureFlagStore";
import { jqXHR } from "../types/Api";

interface Announcement {
  // System Incident
  id: string;
  title: string;
  name: string;
  type: string;

  // Maintenance
  start?: string;
  end?: string;

  // Internal use
  hideme?: boolean;
}

interface StatusIoResponse {
  announcements: Announcement[];
}

export const AnnouncementBar: FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  const [deviceId, region, timezone] = useSessionStore(
    (state) => [state.device?.id, state.device?.region ?? "", state.device?.timezone ?? ""] as const
  );

  const showAnnouncements = useFeatureFlagStore((state) => state.flags && !state.flags?.["hide-status-page-banner"]);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (deviceId && showAnnouncements) {
      fetchAnnouncements();

      pollingIntervalRef.current = setInterval(() => {
        // Fetch the status of incidents and maintenances for the announcement banners, this does not need to be frequent, currently set to 5 minutes
        fetchAnnouncements();
      }, parseInt(process.env.REACT_APP_STATUS_PAGE_POLL_INTERVAL ?? "300000"));
    } else {
      cancelAnnouncementsFetch();
    }

    return cancelAnnouncementsFetch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, showAnnouncements]);

  const fetchAnnouncements = () => {
    Api.get(
      "/announcements",
      (result: StatusIoResponse) => {
        statusChange(result);
      },
      (_err: unknown, jqXHR: jqXHR) => {
        console.log(`Announcements fetch failed: %o %s`, jqXHR?.status, String(jqXHR?.responseJSON?.error ?? ""));
      }
    );
  };

  const cancelAnnouncementsFetch = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = undefined;
    }

    setAnnouncements([]);
  };

  // api-dispatcher always concatenates the region to the title of the announcement, as defined in statusio_handler.py
  const statusIoRegion = useMemo(() => {
    switch (region) {
      case "us-1": {
        // New Zealand features two different timezones
        const nzTimezones = ["Pacific/Auckland", "Pacific/Chatham"];
        if (nzTimezones.includes(timezone)) {
          return "New Zealand";
        }
        // Non New Zealand timezones are considered USA
        return "USA";
      }
      case "syd-2": {
        return "Australia";
      }
      case "uk-1": {
        return "United Kingdom";
      }
      // If the region is a development region, show all announcements
      default: {
        return "";
      }
    }
  }, [region, timezone]);

  const statusChange = (statusio: StatusIoResponse) => {
    if (!statusio.announcements) {
      return;
    }

    setAnnouncements((announcements) => {
      const shouldDisplay = (announcement: Announcement) => {
        // If the region is a production region, only show announcements that are for the current region
        const productionRegions = ["us-1", "syd-2", "uk-1"];
        if (productionRegions.includes(region)) {
          return announcement.title.includes(statusIoRegion);
        }

        // If the region is a development region, show all announcements
        return true;
      };

      const announcementsNew: typeof announcements = [];

      statusio.announcements.forEach((announcement) => {
        const existing = announcements.find((a) => a.id === announcement.id);
        if (existing) {
          announcement.hideme = existing.hideme;
        }

        if (!shouldDisplay(announcement)) {
          return;
        }

        if (announcement.type !== "System Incident") {
          announcementsNew.push(formatTime(announcement));
        } else {
          announcementsNew.push(announcement);
        }
      });

      return announcementsNew;
    });
  };

  const formatTime = (announcement: Announcement) => {
    if (announcement.type === "SYSTEM OUTAGE") {
      return announcement;
    }
    announcement.title = announcement.title.concat(
      dayjs(announcement.start).format("DD/MM/YY HH:mm"),
      " until ",
      dayjs(announcement.end).format("DD/MM/YY HH:mm")
    );
    return announcement;
  };

  const closeAnnouncement = (announcementId: string) => {
    const announcementsNew = [...announcements];

    for (let i = 0; i < announcementsNew.length; i++) {
      if (announcementsNew[i].id === announcementId) {
        announcementsNew[i].hideme = true;
        break;
      }
    }
    setAnnouncements(announcementsNew);
  };

  const handleLink = () => {
    window.open("http://status.linewize.net/", "_blank");
  };

  return (
    <div className="announcement-bar-container" data-testid="announcement-bar-container">
      {announcements
        .filter((a) => !a.hideme)
        .map((announcement) => {
          return (
            <div className="status-bar-open" key={announcement.id}>
              <div className="announcement-type">{announcement.type}</div>
              <div className="announcement-spacer">-</div>
              <div className="announcement-title">{announcement.title}</div>
              <div className="announcement-name">{announcement.name}</div>
              <button type="button" className="announcement-link" onClick={handleLink}>
                VIEW DETAILS
              </button>
              <button onClick={() => closeAnnouncement(announcement.id)} className="ab-close-button" aria-label="Dismiss Announcement">
                <img src={closeIcon} alt="Close announcement" aria-hidden="true" />
              </button>
            </div>
          );
        })}
    </div>
  );
};
